import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { SideManu } from '../components';
import { APP_FEATURES, APP_ROUTES, SIDE_BAR_ITEMS } from '../constants';
import { ISideBarItem } from '../interface/main.interface';
import Navbar from './../components/TopBbar';
import GracePeriodBanner, { GRACE_PERIOD_SEVERITY } from '../components/GracePeriodBanner';
import { useGracePeriodStatus } from '../hooks/useGracePeriodStatus';
import { useSubscriptionPolling } from '../hooks/useSubscriptionPolling';
import { ExpiredSubscriptionOverlay } from '../components/SubscriptionFeatureGuard';
import { hasPrivilege } from '../utility/hasPrivilege';
// import AgentFAB from '../components/agent/AgentFAB';
// import AgentDrawer from '../components/agent/AgentDrawer';
import '../styles/design-tokens.css';
import '../styles/animations.css';

// Custom route titles for routes not in sidebar
const CUSTOM_ROUTE_TITLES: Record<string, string> = {};

// Same derivation the sidebar uses, so the header title translates with the same keys
const menuKey = (label?: string): string =>
  !label
    ? ''
    : 'menu.' +
      label.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const MainLayout: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isMenuPinned, setIsMenuPinned] = useState(true); // Pinned open by default
  const [isHovering, setIsHovering] = useState(false);

  // Initialize centralized subscription polling (single timer for entire app)
  useSubscriptionPolling();

  // Get grace period status from Redux (no polling, just reading state)
  const { status: gracePeriodStatus, loading: gracePeriodLoading } = useGracePeriodStatus();
  // The common Home landing is now the universal post-login page for EVERY role,
  // including staff whose org may be in grace/expired. It must never HARD-BLOCK
  // any role, so the restricted overlay (the hard block) stays bypassed here — a
  // no-subscription student/guardian still gets a clean page. The grace-period
  // banner, however, is only informational and IS shown on /home so staff still
  // see their billing status. (Overlay bypass only — banner is not gated here.)
  const isOverlayNeutralRoute = location.pathname === APP_ROUTES.HOME;
  const shouldBypassOverlay =
    isOverlayNeutralRoute ||
    location.pathname === APP_ROUTES.BILLING_AND_PLANS ||
    location.pathname.startsWith(`${APP_ROUTES.BILLING_AND_PLANS}/`);
  const shouldShowRestrictedOverlay =
    !gracePeriodLoading && gracePeriodStatus?.isRestricted && !shouldBypassOverlay;

  const handleMenuClick = () => {
    setIsMenuPinned((prev) => !prev);
  };

  useEffect(() => {
    const checkScreenSize = () => {
      if (window.innerWidth < 1280) {
        setIsMenuPinned(false);
      } else {
        setIsMenuPinned(true);
      }
    };

    checkScreenSize();

    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const isMenuExpanded = isMenuPinned || isHovering;

  const findCurrentRouteLabel = (currentPath: string, items: ISideBarItem[]): string | null => {
    for (const item of items) {
      if (item.route) {
        const routePattern = new RegExp(`^${item.route.replace(/:\w+/, '[^/]+')}$`);
        if (routePattern.test(currentPath)) return item.label;
      }
      if (item.children) {
        const childLabel = findCurrentRouteLabel(currentPath, item.children);
        if (childLabel) return childLabel;
      }
    }
    return null;
  };

  const pageTitle = useMemo(() => {
    // First check custom route titles for dynamic routes
    for (const [route, title] of Object.entries(CUSTOM_ROUTE_TITLES)) {
      const routePattern = new RegExp(`^${route.replace(/:\w+/g, '[^/]+')}$`);
      if (routePattern.test(location.pathname)) {
        return t(menuKey(title), title);
      }
    }
    // Then check sidebar items
    const label = findCurrentRouteLabel(location.pathname, SIDE_BAR_ITEMS);
    return label ? t(menuKey(label), label) : '';
  }, [location.pathname, t]);

  return (
    <div className="flex h-screen bg-[#F5F5F5] overflow-hidden">
      {/* Sidebar - Full Height */}
      <div
        className="h-full fixed top-0 left-0 z-20 flex-shrink-0"
        onMouseEnter={() => {
          if (!isMenuPinned) setIsHovering(true);
        }}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div
          className={`h-full transition-all duration-300 ease-in-out flex-shrink-0 ${
            isMenuExpanded ? 'w-72' : 'w-20'
          }`}
        >
          <SideManu isExpanded={isMenuExpanded} isPinned={isMenuPinned} />
        </div>
      </div>

      {/* Right Side: Header + Content + Footer */}
      <div
        className={`flex flex-col flex-1 h-full overflow-hidden transition-all duration-300 ease-in-out ${
          isMenuPinned ? 'xl:ml-72 ml-20' : 'ml-20'
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0">
          <Navbar title={pageTitle} handleMenuClick={handleMenuClick} />
        </div>

        {/* Content Area */}
        <div
          id="scrollable-outlet"
          className="flex-1 overflow-y-auto overflow-x-hidden p-4 min-h-0"
        >
          {shouldShowRestrictedOverlay && (
              <ExpiredSubscriptionOverlay
                canManageBilling={hasPrivilege(APP_FEATURES.BILLING_MANAGEMENT)}
                unpaidInvoiceCount={gracePeriodStatus?.unpaidInvoiceCount || 0}
                outstandingAmount={gracePeriodStatus?.outstandingAmount || 0}
                className="absolute inset-0"
              />
            )}
          {/* Subscription Status Banner — single banner, single source of truth.
              Shown on ALL routes, including the common Home landing, so staff
              whose org is in grace/expired still see their billing status there.
              (Informational only — the hard-block overlay stays bypassed on /home.) */}
          {!gracePeriodLoading &&
            gracePeriodStatus &&
            gracePeriodStatus.isRestricted === false &&
            (gracePeriodStatus.isInGracePeriod || gracePeriodStatus.unpaidInvoiceCount > 0) && (
              <div className="pb-4">
                <GracePeriodBanner
                  isRestricted={gracePeriodStatus.isRestricted}
                  isInGracePeriod={gracePeriodStatus.isInGracePeriod}
                  unpaidInvoiceCount={gracePeriodStatus.unpaidInvoiceCount}
                  showViewPlansButton={location.pathname !== APP_ROUTES.BILLING_AND_PLANS}
                  severity={gracePeriodStatus.severity as GRACE_PERIOD_SEVERITY}
                  gracePeriodEndDate={
                    gracePeriodStatus.gracePeriodEndDate
                      ? new Date(gracePeriodStatus.gracePeriodEndDate).toISOString()
                      : undefined
                  }
                  daysRemaining={gracePeriodStatus.daysRemaining}
                  outstandingAmount={gracePeriodStatus.outstandingAmount}
                />
              </div>
            )}

          <div className="relative max-w-full min-h-full">
            <Outlet />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-[#3E3E3E] w-full py-3 px-6 text-white flex-shrink-0 flex items-center justify-center">
          <span className="text-sm font-medium">www.classveew.com</span>
        </div>
      </div>

      {/* AI Marketing Agent — moved to full-page route /classveew-ai */}
      {/* <AgentFAB /> */}
      {/* <AgentDrawer /> */}
    </div>
  );
};

export default MainLayout;
